import CustomFile from 'components/file/CustomFile'
import CustomText from 'components/text/CustomText'
import styled from 'styled-components'
import { FixMeLater } from 'types'
import { IUserData } from 'types/user.types'
import { updateContentText } from 'utils/helpers/messageHelper'
// import { useEffect, useState, useMemo } from 'react'
// import axiosApi from 'app/axiosApi'
// import { useAppSelector } from 'app/hooks'

interface IContentProps {
  data: {
    header?: string
    text: string
    files: FixMeLater[]
    mentionedUsers: Array<string>
  }
  onShowUserInfo: (userId: string) => void
  users: IUserData
  isCollapsible?: boolean
}

const groupFilesByType = (files: FixMeLater[]) => {
  const groups = {
    media: [] as FixMeLater[],
    documents: [] as FixMeLater[],
  }

  files.forEach((file) => {
    if (!file) {
      return; // null file'ı atla
    }
    const mimetype = file?.mimetype || file?.type || ''
    if (mimetype?.includes('image') || mimetype?.includes('video')) {
      groups.media.push(file)
    } else {
      groups.documents.push(file)
    }
  })

  return groups
}

const Content = ({ data, onShowUserInfo, users, isCollapsible = false }: IContentProps) => {
  const textByRepliedUsers: FixMeLater = updateContentText(
    data?.text,
    data?.mentionedUsers,
    undefined,
    users,
    onShowUserInfo,
  )

  const groupedFiles = data?.files?.length > 0 ? groupFilesByType(data.files) : null

  // Presigned URL'li dosya listeleri
  const mediaWithPresigned = groupedFiles?.media || [];
  const documentsWithPresigned = groupedFiles?.documents || [];

  // Dosyaların orijinal dizideki indeks bilgilerini tutan harita
  const fileIndexMap = new Map<string, number>()

  if (data?.files?.length) {
    data.files.forEach((file, index) => {
      const fileId = file?.lastModified || file?.name || file?.originalName || index
      fileIndexMap.set(String(fileId), index)
    })
  }

  // Bir dosyanın orijinal dizideki indeksini bulmak için yardımcı fonksiyon
  const getOriginalIndex = (file: FixMeLater, fallbackIndex: number) => {
    const fileId = file?.lastModified || file?.name || file?.originalName || fallbackIndex
    return fileIndexMap.has(String(fileId)) ? fileIndexMap.get(String(fileId)) : fallbackIndex
  }

  return (
    <Wrapper>
      {data?.header && (
        <HeaderWrapper>
          <CustomText textAlign="center" typography="header1" isCollapsible={isCollapsible}>
            {data.header}
          </CustomText>
        </HeaderWrapper>
      )}
      <TextWrapper>
        <CustomText typography="body2" isCollapsible={isCollapsible}>
          {textByRepliedUsers}
        </CustomText>
      </TextWrapper>

      {groupedFiles && (
        <FilesWrapper onClick={(event) => event.stopPropagation()}>
          {/* Resimler ve Videolar */}
          {mediaWithPresigned.length > 0 && (
            <GridLayout>
              {mediaWithPresigned.map((file: FixMeLater, groupIndex: number) => {
                let url = '';
                if (file.url) {
                  url = file.url;
                } else if (file instanceof File || file instanceof Blob) {
                  url = URL.createObjectURL(file);
                }
                const originalIndex = getOriginalIndex(file, groupIndex);
                return (
                  <div key={`media-${file?._id || file?.lastModified || groupIndex}-${url}`}>
                    <CustomFile
                      index={originalIndex}
                      files={data.files}
                      sentAt={file.sentAt}
                      source={url}
                      mimetype={file?.mimetype || file?.type || ''}
                      preset="post"
                      size={file?.size}
                      filename={file.originalName}
                      thumbnail={file?.thumbnail}
                    />
                  </div>
                );
              })}
            </GridLayout>
          )}

          {/* Dökümanlar */}
          {documentsWithPresigned.length > 0 && (
            <DocumentList>
              {documentsWithPresigned.map((file: FixMeLater, groupIndex: number) => {
                let url = '';
                if (file.url) {
                  url = file.url;
                } else if (file instanceof File || file instanceof Blob) {
                  url = URL.createObjectURL(file);
                }
                const originalIndex = getOriginalIndex(file, groupIndex);
                return (
                  <DocumentItem key={`doc-${file?._id || file?.lastModified || groupIndex}-${url}`}>
                    <CustomFile
                      index={originalIndex}
                      files={data.files}
                      sentAt={file.sentAt}
                      source={url}
                      mimetype={file?.mimetype || file?.type || ''}
                      preset="post"
                      size={file?.size}
                      filename={file.originalName}
                    />
                  </DocumentItem>
                );
              })}
            </DocumentList>
          )}
        </FilesWrapper>
      )}
    </Wrapper>
  )
}

export default Content

const Wrapper = styled.div`
  margin: 8px 0px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const HeaderWrapper = styled.div`
  margin-bottom: 4px;
`

const TextWrapper = styled.div`
  margin-bottom: 8px;
`

const FilesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 8px;
`

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  background-color: white;
  &:hover {
    background-color: #f0f0f0;
  }
`

const GridLayout = styled.div`
  display: grid;
  gap: 10px;
  width: 100%;
  
  /* Tüm div'ler için ortak stil */
  > div {
    overflow: hidden;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
  }
  
  /* Tek resim durumu */
  ${props => props.children && (props.children as FixMeLater).length === 1 && `
    grid-template-columns: 1fr;
    
    > div:nth-child(1) {
      height: 350px;
    }
  `}
  
  /* İki resim durumu */
  ${props => props.children && (props.children as FixMeLater).length === 2 && `
    grid-template-columns: 1fr 1fr;
    
    > div:nth-child(1) {
      grid-column: 1;
      grid-row: 1;
    }
    
    > div:nth-child(2) {
      grid-column: 2;
      grid-row: 1;
    }
  `}
  
  /* Üç resim durumu */
  ${props => props.children && (props.children as FixMeLater).length === 3 && `
    grid-template-columns: 1fr 1fr;
    
    > div:nth-child(1) {
      grid-column: 1;
      grid-row: 1;
    }
    
    > div:nth-child(2) {
      grid-column: 2;
      grid-row: 1;
    }
    
    > div:nth-child(3) {
      grid-column: span 2;
      grid-row: 2 / 3;
    }
  `}
  
  /* Dört veya daha fazla resim durumu */
  ${props => props.children && (props.children as FixMeLater).length >= 4 && `
    grid-template-columns: 1fr 1fr;
    
    > div:nth-child(1) {
      grid-column: 1;
      grid-row: 1;
    }
    
    > div:nth-child(2) {
      grid-column: 2;
      grid-row: 1;
    }
    
    > div:nth-child(3) {
      grid-column: span 2;
      grid-row: 2 / 3;
    }
    
    > div:nth-child(4) {
      grid-column: 1 / 2;
      grid-row: 2 / 4;
    }
  `}
`
